import React from 'react';
import { GetServerSideProps, Redirect } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { AcceptedQueryTypes } from '@commercetools/frontend-sdk/lib/types/Query';
import { getSession, useSession, signOut } from 'next-auth/react';
import { serverSideTranslations } from '../lib/serverSideTranslations';

/* eslint-disable @typescript-eslint/ban-ts-comment */
//@ts-ignore
import { renderToString } from 'react-dom/server';
import { InstantSearchServerState } from 'react-instantsearch-hooks';
import { getServerState } from 'react-instantsearch-hooks-server';
import GASnippet from 'components/headless/GASnippet';
import { IconsProvider } from 'context/icons';
import { useFormat } from '@loomstateorg/shared-frontend/hooks/useFormat';
import { performLogout, isSSOEnabled } from '@loomstateorg/shared-frontend';
import { sdk } from 'sdk';
import { Category } from 'types/category';
import { createClient, PageDataResponse, ResponseError } from 'frontastic';
import useAccount from '@loomstateorg/shared-frontend/hooks/useAccount';
import { GlobalCTProvider } from 'frontastic/context/globalContext';
import { FrontasticRenderer } from 'frontastic/lib/renderer';
import { tastics } from 'frontastic/tastics';
import ProductListTastic, { Props as ProductListTasticProps } from 'frontastic/tastics/products/product-list-algolia';
import styles from './slug.module.css';
import { Log } from '@loomstateorg/shared-frontend/helpers/errorLogger';

type SlugProps = {
  // This needs an overhaul. Can be too many things in my opinion (*Marcel)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
  // data: RedirectResponse | PageDataResponse | ResponseError | { ok: string; message: string } | string;
  locale: string;
  ssoError?: string;
  ssoErrorEmail?: string;
};

export default function Slug({ data, ssoError, ssoErrorEmail }: SlugProps) {
  const { formatMessage } = useFormat({ name: 'common' });
  const { formatMessage: formatAccountMessage } = useFormat({ name: 'account' });
  const router = useRouter();
  const { logout } = useAccount();
  const { data: session } = useSession();

  const logoutRedirect = async () => {
    // Use direct performLogout function to avoid SessionProvider dependency
    await performLogout(null, logout);
  };

  if (ssoError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="w-full max-w-md rounded-lg  px-8 py-12 shadow-lg" data-testid="sso-error">
          <div className="text-center">
            <h1 className="mb-6 text-3xl font-bold text-red-600" data-testid="error-title">
              {formatAccountMessage({ id: 'sso.error.accountNotFound', defaultMessage: 'Account Not Found' })}
            </h1>
            <p className="mb-8 leading-relaxed text-gray-700" data-testid="error-message">
              {ssoError === 'sso.error.accountNotFoundMessage' 
                ? formatAccountMessage({ 
                    id: 'sso.error.accountNotFoundMessage', 
                    defaultMessage: "We couldn't find an account associated with {{email}}. Please contact support to set up your account.",
                    values: { email: ssoErrorEmail || 'your email' }
                  })
                : ssoError === 'sso.error.generalError'
                ? formatAccountMessage({ 
                    id: 'sso.error.generalError', 
                    defaultMessage: 'There was an error accessing your account. Please try again later or contact support.'
                  })
                : ssoError
              }
            </p>
          </div>
          <div className="flex items-center justify-center gap-6">
            <a
              data-testid="contact-support"
              href="mailto:chipotle.support@loomstate.org"
              className="rounded-lg bg-red-600 px-6 py-3 font-medium text-white shadow-sm transition-colors duration-200 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-200 focus:ring-offset-2"
            >
              {formatAccountMessage({ id: 'sso.error.contactSupport', defaultMessage: 'Contact Support' })}
            </a>
            <span className="font-medium text-gray-500">
              {formatAccountMessage({ id: 'sso.error.or', defaultMessage: 'or' })}
            </span>
            <button
              data-testid="try-again"
              onClick={() => logoutRedirect()}
              className="rounded-lg px-6 py-3 font-medium text-red-600 underline underline-offset-4 transition-colors duration-200 hover:text-red-700 focus:outline-none focus:ring-2 focus:ring-red-200 focus:ring-offset-2"
            >
              {formatAccountMessage({ id: 'sso.error.tryAgain', defaultMessage: 'Try again' })}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!data || typeof data === 'string') {
    return (
      <>
        <h1 className="mt-2 text-4xl font-extrabold tracking-tight text-gray-900">Internal Error</h1>
        <p className="mt-2 text-lg">{data}</p>
        <p className="mt-2 text-lg">Check the logs of your Frontastic CLI for more details.</p>
      </>
    );
  }

  if (!data?.ok && data?.message) {
    return (
      <>
        <h1 className="mt-2 text-4xl font-extrabold tracking-tight text-gray-900">Internal Error</h1>
        <p className="mt-2 text-lg">{data.message}</p>
        <p className="mt-2 text-lg">Check the logs of your Frontastic CLI for more details.</p>
      </>
    );
  }

  return (
    <>
      <Head>
        <title>{formatMessage({ id: 'meta.title', defaultMessage: 'Chipotle B2B' })}</title>
        <meta
          name="description"
          content={formatMessage({ id: 'meta.desc', defaultMessage: 'Find largest home collections here!' })}
        />
      </Head>
      <GASnippet />
      <GlobalCTProvider>
        <IconsProvider>
          <FrontasticRenderer data={data} tastics={tastics} wrapperClassName={styles.gridWrapper} />
        </IconsProvider>
      </GlobalCTProvider>
    </>
  );
}

export const getServerSideProps: GetServerSideProps = async ({ params, locale, query, req, res, resolvedUrl }) => {
  sdk.configureForNext(locale as string);

  const frontastic = createClient();
  const isSSOEnabled = process.env.NEXT_PUBLIC_SSO_ENABLED && process.env.NEXT_PUBLIC_SSO_ENABLED == 'true';

  let session = null;
  let account = null;
  let customerByEmail = null;

  if (isSSOEnabled) {
    session = await getSession({ req });

    if (session?.user?.email) {
      try {
        customerByEmail = await frontastic.getAccountByCustomerEmail(req, res);

        if (!customerByEmail || !customerByEmail.loggedIn) {
          return {
            props: {
              ssoError: 'sso.error.accountNotFoundMessage',
              ssoErrorEmail: session.user.email,
            },
          };
        }
      } catch (error) {
        console.error('Error fetching customer account:', error);
        return {
          props: {
            ssoError: 'sso.error.generalError',
          },
        };
      }
    }
  } else {
    account = await frontastic.getAccount(req, res);
  }

  const [data, categories, customerGroup, headquartersAddress] = await Promise.all([
    frontastic.getRouteData(params?.slug as string[], query as AcceptedQueryTypes, req, res),
    frontastic.getCategories(req, res),
    frontastic.getCustomerGroup(req, res),
    frontastic.getHeadquartersAddress(req, res),
  ]);

  // Check if user is logged in (either via SSO or traditional auth)
  const isLoggedIn = customerByEmail?.loggedIn || account?.loggedIn;
  
  if (!isLoggedIn) {
    // Define variables for better readability
    const PUBLIC_ROUTES = ['login', 'reset-password'];

    const currentRoute = (params?.slug as string[] | undefined)?.filter(Boolean)[0];
    const isPublicRoute = currentRoute && PUBLIC_ROUTES.includes(currentRoute);
    
    if (!params || !params.slug || !isPublicRoute) {
      return {
        redirect: {
          destination: '/login',
          statusCode: 307,
        } as Redirect,
      };
    }
  }

  if (data instanceof ResponseError && data.getStatus() === 404) {
    return { notFound: true };
  }
  if (data != null && typeof data === 'object' && 'target' in data && 'statusCode' in data) {
    return {
      redirect: {
        destination: data.target,
        statusCode: data.statusCode,
      } as Redirect,
    };
  }
  if (data instanceof Error) {
    // @TODO: Render nicer error page in debug mode, which shows the error to
    // the developer and also outlines how to debug this (take a look at
    // frontastic-CLI).
    Log.error(new Error('Error retrieving data: '), data);
    return {
      notFound: true,
    };
  }

  if (typeof data === 'string') {
    return {
      props: {
        data: { error: data },
        error: data,
      },
    };
  }

  const protocol = req.headers.referer?.split('://')[0] || 'https';

  const serverUrl = `${protocol}://${req.headers.host}${resolvedUrl}`;

  /* Algolia */
  const plpTasticKey = 'commercetools/ui/products/product-list-algolia';

  const plpConfiguration = (data as PageDataResponse).page?.sections.main.layoutElements
    .find((layoutElement) => layoutElement.tastics.find((tastic) => tastic.tasticType === plpTasticKey))
    ?.tastics.find((tastic) => tastic.tasticType === plpTasticKey)?.configuration as Partial<
    ProductListTasticProps['data']
  >;

  let serverState: null | InstantSearchServerState = null;

  if (plpConfiguration) {
    const slug = (params?.slug as string[])?.[(params?.slug?.length as number) - 1];
    const searchQuery = (query.q as string) ?? '';

    if (slug) plpConfiguration.slug = slug;
    if (searchQuery) plpConfiguration.searchQuery = searchQuery;

    serverState = await getServerState(
      <ProductListTastic
        serverUrl={serverUrl}
        categories={(categories.items as Category[]) ?? []}
        data={{
          slug,
          searchQuery,
          facetsConfiguration: plpConfiguration.facetsConfiguration ?? [],
          pricesConfiguration: plpConfiguration.pricesConfiguration ?? [],
        }}
      />,
      { renderToString },
    );
  }

  const translations = await serverSideTranslations(locale as string, [
    'common',
    'cart',
    'product',
    'payment',
    'checkout',
    'account',
    'customer-support',
    'error',
    'success',
    'wishlist',
    'newsletter',
    'orders',
    'thank-you',
    'dashboard',
  ]);

  return {
    props: {
      data: { ...data, categories, ...customerGroup, headquartersAddress, serverUrl, serverState },
      locale: locale,
      ...translations,
    },
  };
};
